import { Dialog, Toast } from 'vant'
import dsBridgeHelper from '@/utils/dsBridgeHelper'

export const CAMERA_PERMISSION_DENIED_ERROR = 'camera_permission_denied'

let activePermissionDialogPromise = null

function resolvePermissionMessage(vm) {
  if (vm && typeof vm.$t === 'function') {
    return vm.$t('camera_permission_required_for_verification')
  }

  return 'To complete the current identity verification, we need access to your camera for facial recognition or for capturing your ID document for OCR verification. If camera permission is not granted, the system will be unable to collect the required verification information, and you will not be able to continue to the next step.'
}

function resolveDialogTitle(vm) {
  if (vm && typeof vm.$t === 'function') {
    return vm.$t('permission_request_title')
  }

  return 'Request Permission'
}

function resolveConfirmText(vm) {
  if (vm && typeof vm.$t === 'function') {
    return vm.$t('confirm')
  }

  return 'Confirm'
}

function resolveCancelText(vm) {
  if (vm && typeof vm.$t === 'function') {
    return vm.$t('cancel')
  }

  return 'Cancel'
}

async function showPermissionDialog(vm, message) {
  if (activePermissionDialogPromise) {
    return activePermissionDialogPromise
  }

  const dialogTitle = resolveDialogTitle(vm)
  const confirmButtonText = resolveConfirmText(vm)
  const cancelButtonText = resolveCancelText(vm)

  // 清掉带 forbidClick 的 loading，避免遮罩层拦截弹窗按钮点击。
  Toast.clear()
  Dialog.close()

  activePermissionDialogPromise = Dialog.confirm({
    title: dialogTitle,
    message,
    confirmButtonText,
    cancelButtonText,
    showCancelButton: true
  })
    .then(() => true)
    .catch(() => false)
    .finally(() => {
      activePermissionDialogPromise = null
    })

  return activePermissionDialogPromise
}

async function getBrowserCameraPermissionState() {
  if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
    return 'prompt'
  }

  try {
    const status = await navigator.permissions.query({ name: 'camera' })
    return status && status.state ? status.state : 'prompt'
  } catch (error) {
    return 'prompt'
  }
}

async function requestBrowserCameraPermission() {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    return false
  }

  let stream = null
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: true })
    return true
  } catch (error) {
    return false
  } finally {
    if (stream && typeof stream.getTracks === 'function') {
      stream.getTracks().forEach(track => track.stop())
    }
  }
}

async function ensureAppCameraPermission(vm, message) {
  const hasPermission = await dsBridgeHelper.checkCameraPermissionAsync()
  if (hasPermission) {
    return true
  }

  const shouldRequestPermission = await showPermissionDialog(vm, message)
  if (!shouldRequestPermission) {
    return false
  }

  try {
    const granted = await dsBridgeHelper.requestCameraPermission()
    if (granted) {
      return true
    }
  } catch (error) {
    // ignore and fallback to settings
  }

  // 首次确认后若系统授权被拒绝，直接跳转系统设置页。
  try {
    await dsBridgeHelper.openAppSettings()
  } catch (error) {
    // open settings fail 时只拦截流程，不再抛错打断业务
  }

  return false
}

async function ensureBrowserCameraPermission(vm, message) {
  const state = await getBrowserCameraPermissionState()
  if (state === 'granted') {
    return true
  }

  const shouldRequestPermission = await showPermissionDialog(vm, message)
  if (!shouldRequestPermission) {
    return false
  }

  return requestBrowserCameraPermission()
}

/**
 * 统一相机权限校验入口：
 * 1. 先检查当前权限状态；
 * 2. 未授权时先弹窗提示，再触发授权；
 * 3. 未授权成功则返回 false 由调用方拦截业务流程。
 */
export async function ensureCameraPermission(vm) {
  const message = resolvePermissionMessage(vm)

  if (dsBridgeHelper.isApp()) {
    return ensureAppCameraPermission(vm, message)
  }

  return ensureBrowserCameraPermission(vm, message)
}

export async function ensureCameraPermissionOrThrow(vm) {
  const granted = await ensureCameraPermission(vm)
  if (!granted) {
    throw new Error(CAMERA_PERMISSION_DENIED_ERROR)
  }

  return true
}
